// Contacts tab (docs/06 §2): the full contact list with presence + call, the
// "Your ID" card and the add-friend entry (that's where adding people lives
// now), and incoming friend requests with the requests badge. Tapping a
// contact opens its detail screen; blocked contacts are marked and can't be
// speed-dialed from here without going through detail.
//
// Offline-first: all three lists boot from the local cache (last successful
// fetch) so a cold start with the backend unreachable is never empty; fetches
// only update the view (and write the cache back). Cached presence is already
// degraded to offline (serverCache.ts). Request accept/decline still need the
// server — the cache is visibility only.

import { useCallback, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Friend, PresenceStatus } from '@merkury/protocol';
import {
  getCachedBlocks,
  getCachedFriends,
  getCachedRequests,
  setCachedBlocks,
  setCachedFriends,
  setCachedRequests,
  type CachedRequests,
} from '../../storage/serverCache.js';
import { useAccount } from '../../shell/accountContext.js';
import { useT, type TFunc } from '../../i18n/index.js';
import { presenceOf } from '@merkury/brain/api/liveEvents.js';
import { useScreens } from '../ScreensContext.js';
import { useStore } from '../ui/useStore.js';
import { useDialer } from '../ui/useDialer.js';
import { useServerReload } from '../ui/useServerReload.js';
import { useAliases } from '../ui/useAliases.js';
import { effectiveName } from '../ui/peerName.js';
import { PullToRefresh } from '../ui/PullToRefresh.js';
import { PeerAvatar } from '../ui/PeerAvatar.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Glyph, PHONE_PATH, PLUS_PATH, USERS_PATH, INBOX_PATH } from '../ui/icons.js';

function presenceLabel(t: TFunc, status: PresenceStatus): string {
  if (status === 'app') return t('contactDetail.presenceApp');
  if (status === 'reachable') return t('contactDetail.presenceReachable');
  return t('contactDetail.presenceOffline');
}

export function ContactsScreen(): ReactNode {
  const t = useT();
  const { state } = useAccount();
  const { api, engine, liveEvents } = useScreens();
  const navigate = useNavigate();
  const account = state.status === 'ready' ? state.account : undefined;

  const engineState = useStore(engine.state);
  const live = useStore(liveEvents.state);
  const aliases = useAliases();
  const { notice, dial } = useDialer();

  const [friends, setFriends] = useState<Friend[]>(() => getCachedFriends()?.data ?? []);
  const [requests, setRequests] = useState<CachedRequests>(
    () => getCachedRequests()?.data ?? { incoming: [], outgoing: [] },
  );
  const [blocked, setBlocked] = useState<string[]>(() => getCachedBlocks()?.data ?? []);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [friendsRes, requestsRes] = await Promise.all([
        api.listFriends(),
        api.listFriendRequests(),
      ]);
      setFriends(friendsRes.friends);
      setRequests(requestsRes);
      setCachedFriends(friendsRes.friends);
      setCachedRequests(requestsRes);
      setError(null);
    } catch {
      setError(t('sync.refreshError'));
    }
    try {
      const res = await api.listBlocks();
      setBlocked(res.blocked);
      setCachedBlocks(res.blocked);
    } catch {
      // keep last known blocked set (offline-tolerant)
    }
  }, [api, t]);

  useServerReload(reload);

  const presenceFor = (friend: Friend): PresenceStatus => presenceOf(live, friend.userId, friend);

  async function callFriend(friend: Friend): Promise<void> {
    // Yellow (reachable) friends get no offline warning — their phone rings.
    const hint =
      presenceFor(friend) === 'offline'
        ? t('call.offlineHint', { name: effectiveName(friend, aliases) })
        : undefined;
    await dial(friend.userId, hint);
  }

  async function handleCopyUuid(): Promise<void> {
    if (!account?.userId) return;
    try {
      await navigator.clipboard.writeText(account.userId);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      // clipboard API unavailable — the UUID is still visible/selectable
    }
  }

  return (
    <PullToRefresh onRefresh={reload}>
      <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-5 p-4">
        <header className="flex items-center gap-2 px-1 pt-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('contacts.title')}</h1>
          {engineState.signaling !== 'connected' && (
            <span
              role="status"
              aria-label={t('contacts.offlineAria')}
              title={t('contacts.signalingDown')}
              className="inline-block h-2 w-2 rounded-full bg-muted"
            />
          )}
        </header>

        <section className="flex items-center gap-3 rounded-2xl bg-surface p-4">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted">
              {t('contacts.yourId')}
            </p>
            <p className="mt-0.5 truncate font-mono text-sm text-ink" data-testid="own-uuid">
              {account?.userId ?? t('contacts.registering')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCopyUuid}
            className="min-h-touch shrink-0 rounded-xl bg-raised px-4 text-sm font-medium text-ink active:bg-edge"
          >
            {copyFeedback ? t('contacts.copied') : t('contacts.copy')}
          </button>
        </section>

        {notice && (
          <p role="status" className="text-sm text-warn">
            {notice}
          </p>
        )}
        {error && (
          <p role="status" className="text-sm text-muted">
            {error}
          </p>
        )}

        <section aria-label={t('contacts.title')} className="flex flex-col gap-2">
          <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">
            {t('contacts.allContacts')}
          </h2>
          {friends.length === 0 && (
            <EmptyState
              icon={<Glyph d={USERS_PATH} />}
              title={t('contacts.noContacts')}
              help={t('contacts.noContactsHelp')}
            />
          )}
          <ul className="flex flex-col gap-1.5">
            {friends.map((friend) => {
              const isBlocked = blocked.includes(friend.userId);
              const presence: PresenceStatus = isBlocked ? 'offline' : presenceFor(friend);
              const name = effectiveName(friend, aliases);
              return (
                <li key={friend.userId} className="flex items-center rounded-2xl bg-surface">
                  <button
                    type="button"
                    aria-label={t('contacts.openAria', { name })}
                    onClick={() => navigate(`/contact/${friend.userId}`)}
                    className="flex min-h-touch flex-1 items-center gap-3 rounded-2xl p-2.5 text-left active:bg-raised"
                  >
                    <PeerAvatar
                      userId={friend.userId}
                      name={name}
                      {...(friend.avatarSeed !== undefined ? { avatarSeed: friend.avatarSeed } : {})}
                      presence={presence}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block truncate font-medium ${isBlocked ? 'text-muted line-through' : 'text-ink'}`}
                      >
                        {name}
                      </span>
                      <span className="block truncate text-xs text-muted">
                        {isBlocked ? t('contacts.blocked') : presenceLabel(t, presence)}
                      </span>
                    </span>
                  </button>
                  {!isBlocked && (
                    <button
                      type="button"
                      aria-label={t('contacts.callAria', { name })}
                      onClick={() => void callFriend(friend)}
                      className="mr-1.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-accent active:bg-raised"
                    >
                      <Glyph d={PHONE_PATH} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <section aria-label={t('contacts.friendRequests')} className="flex flex-col gap-2">
          <div className="flex items-center justify-between px-1">
            <h2 className="flex items-center text-xs font-semibold uppercase tracking-wide text-muted">
              {t('contacts.requests')}
              {requests.incoming.length > 0 && (
                <span
                  data-testid="requests-badge"
                  aria-label={t('contacts.requestsBadge', { count: requests.incoming.length })}
                  className="ml-2 rounded-full bg-danger px-2 py-0.5 text-xs font-medium text-ink"
                >
                  {requests.incoming.length}
                </span>
              )}
            </h2>
            <Link
              to="/add"
              aria-label={t('contacts.addFriendAria')}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-raised text-accent active:bg-edge"
            >
              <Glyph d={PLUS_PATH} />
            </Link>
          </div>
          {requests.incoming.length === 0 && (
            <EmptyState
              icon={<Glyph d={INBOX_PATH} />}
              title={t('contacts.noRequests')}
              help={t('contacts.noRequestsHelp')}
            />
          )}
          <ul className="flex flex-col gap-1.5">
            {requests.incoming.map((req) => {
              const name = req.fromDisplayName ?? req.fromUserId;
              return (
                <li key={req.id} className="flex items-center gap-3 rounded-2xl bg-surface p-2.5">
                  <PeerAvatar userId={req.fromUserId} name={name} size={40} />
                  <span className="min-w-0 flex-1 truncate text-ink">{name}</span>
                  <button
                    type="button"
                    onClick={async () => {
                      await api.acceptFriendRequest(req.id);
                      await reload();
                    }}
                    className="min-h-touch rounded-xl bg-accent px-3 text-sm font-medium text-on-accent active:bg-accent-press"
                  >
                    {t('common.accept')}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await api.declineFriendRequest(req.id);
                      await reload();
                    }}
                    className="min-h-touch rounded-xl bg-raised px-3 text-sm text-ink active:bg-edge"
                  >
                    {t('common.decline')}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </main>
    </PullToRefresh>
  );
}
